import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Flame, Gift, Loader2, Lock, Sparkles, Trophy } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { getChallengeStampUrl } from "@/lib/challengeStamps";

// Challenges dashboard. GET /api/challenges/status returns
// { special: [Challenge], general: [Challenge] }. Each challenge has
// { challenge_id, name, description, target, progress, claimed, ready,
//   requires_premium, reward, ... }.
export default function Challenges() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [claimingId, setClaimingId] = useState(null);

  const load = () => {
    setError(null);
    api.get("/challenges/status").then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load challenges."));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const claim = async (ch) => {
    setClaimingId(ch.challenge_id);
    try {
      await api.post(`/challenges/${ch.challenge_id}/claim`);
      toast.success(`Claimed: ${ch.name}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not claim reward.";
      toast.error(detail);
    } finally {
      setClaimingId(null);
    }
  };

  if (error) return <div className="card-elev p-6" style={{ color: "var(--premium)" }}>{error}</div>;
  if (!data) return (
    <div className="max-w-3xl mx-auto py-12 flex justify-center">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--brand)" }} />
    </div>
  );

  const groups = [
    { label: "Special challenges", key: "special", icon: Sparkles },
    { label: "Ongoing challenges", key: "general", icon: Flame },
  ];

  return (
    <div className="flex flex-col gap-6" data-testid="challenges-page">
      <header>
        <div className="text-xs font-extrabold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
          Challenges
        </div>
        <h1 className="text-2xl md:text-3xl font-extrabold mt-1" style={{ color: "var(--on-surface)" }}>
          Earn extra XP
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--on-surface-muted)" }}>
          Finish challenges for bonus XP and exclusive badges.
        </p>
      </header>

      {groups.map((g) => {
        const list = data[g.key] || [];
        if (list.length === 0) return null;
        return (
          <section key={g.key}>
            <h2 className="text-sm font-extrabold uppercase tracking-widest mb-3 inline-flex items-center gap-2"
                style={{ color: "var(--brand)" }}>
              <g.icon className="w-4 h-4" /> {g.label}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
              {list.map((ch) => (
                <ChallengeCard key={ch.challenge_id} ch={ch}
                              onClaim={() => claim(ch)}
                              claiming={claimingId === ch.challenge_id} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function ChallengeCard({ ch, onClaim, claiming }) {
  const progressPct = Math.min(100, Math.round(((ch.progress ?? 0) / (ch.target || 1)) * 100));
  const canClaim = ch.ready && !ch.claimed;
  const locked = ch.requires_premium && !ch.premium_ok;
  // Show the challenge's own printable stamp badge once a camper has
  // completed AND claimed it — this is the "trophy" the user unlocks.
  // Before that we keep the neutral iconography so it feels earned.
  const stampUrl = ch.claimed ? getChallengeStampUrl(ch) : null;
  return (
    <article className="card-elev p-4 flex flex-col md:flex-row md:items-center gap-4"
             data-testid={`challenge-${ch.challenge_id}`}>
      {stampUrl ? (
        <img
          src={stampUrl}
          alt=""
          className="w-16 h-16 shrink-0 object-contain"
          style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.18))" }}
          data-testid={`challenge-stamp-${ch.challenge_id}`}
        />
      ) : (
        <div className="w-12 h-12 shrink-0 rounded-2xl flex items-center justify-center"
             style={{
               background: ch.claimed
                 ? "color-mix(in srgb, var(--brand) 15%, transparent)"
                 : canClaim
                 ? "color-mix(in srgb, var(--premium) 18%, transparent)"
                 : "color-mix(in srgb, var(--brand) 8%, transparent)",
               color: canClaim ? "var(--premium)" : "var(--brand)",
             }}>
          {ch.claimed ? <CheckCircle2 className="w-6 h-6" /> :
           canClaim ? <Gift className="w-6 h-6" /> :
           locked ? <Lock className="w-6 h-6" /> :
           <Trophy className="w-6 h-6" />}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-extrabold text-base leading-tight" style={{ color: "var(--on-surface)" }}>{ch.name}</h3>
          {ch.requires_premium && (
            <span className="text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
                  style={{ background: "color-mix(in srgb, var(--premium) 15%, transparent)", color: "var(--premium)" }}>
              Premium
            </span>
          )}
          {ch.reward && (
            <span className="text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
                  style={{ background: "color-mix(in srgb, var(--brand) 10%, transparent)", color: "var(--brand)" }}>
              +{ch.reward} XP
            </span>
          )}
        </div>
        {ch.description && (
          <p className="text-xs mt-1" style={{ color: "var(--on-surface-muted)" }}>{ch.description}</p>
        )}
        <div className="mt-2">
          <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest mb-1"
               style={{ color: "var(--on-surface-muted)" }}>
            <span>{ch.progress ?? 0}/{ch.target}</span>
            <span>{progressPct}%</span>
          </div>
          <div className="w-full h-1.5 rounded-full overflow-hidden"
               style={{ background: "color-mix(in srgb, var(--brand) 10%, transparent)" }}>
            <div className="h-full rounded-full transition-all"
                 style={{
                   width: `${progressPct}%`,
                   background: ch.claimed ? "var(--brand)" : canClaim ? "var(--premium)" : "color-mix(in srgb, var(--brand) 60%, transparent)",
                 }} />
          </div>
        </div>
      </div>
      <div className="shrink-0">
        {ch.claimed ? (
          <span className="text-[10px] font-extrabold uppercase tracking-widest px-3 py-1.5 rounded-full inline-flex items-center gap-1"
                style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Claimed
          </span>
        ) : canClaim ? (
          <button onClick={onClaim} disabled={claiming}
                  className="btn btn-amber justify-center"
                  data-testid={`challenge-claim-${ch.challenge_id}`}>
            {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Gift className="w-4 h-4" /> Claim</>}
          </button>
        ) : locked ? (
          <span className="text-[10px] font-extrabold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
            Premium only
          </span>
        ) : null}
      </div>
    </article>
  );
}
